import React, { useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  Tabs,
  Tab,
  Alert,
  Snackbar,
  Fade,
  useTheme,
  alpha,
  Tooltip,
  Divider
} from "@mui/material";
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Email as EmailIcon,
  Send as SendIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  Close as CloseIcon
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useAuthToken } from "../../TokenContext";
import { useIntl } from "react-intl";

const PartnerINviteTalent = () => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const intl = useIntl();
  const token = useAuthToken();
  const { isLoggedIn, user } = useSelector((state) => state.auth);
  const history = useHistory();
  
  const [modal, setModal] = useState(false);
  const [activeTab, setActiveTab] = useState(1); // Talent tab is active
  const [removebutton, setRemovebutton] = useState(0);
  const [invite, setinvite] = useState([{ emails: '' }]);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const handleinputchange = (e, index) => {
    const { name, value } = e.target;
    const list = [...invite];
    list[index][name] = value;
    setinvite(list);
  };

  const handleremove = (index) => {
    if (invite.length == 1) {
      setSnackbar({
        open: true,
        message: "At least one email field is required",
        severity: 'warning'
      });
      setRemovebutton(1);
      return;
    }
    
    const list = [...invite];
    list.splice(index, 1);
    setinvite(list);
  };

  const handleaddclick = () => {
    if (invite.length >= 3) {
      setSnackbar({
        open: true,
        message: "Maximum limit is 3 invitations",
        severity: 'warning'
      });
      return;
    }
    
    setinvite([...invite, { emails: '' }]);
  };

  const validateEmails = () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const filledEmails = invite.filter(item => item.emails.trim() !== "");
    
    if (filledEmails.length === 0) {
      setSnackbar({
        open: true,
        message: "Please enter at least one email address",
        severity: 'error'
      });
      return false;
    }
    
    for (let item of filledEmails) {
      if (!emailRegex.test(item.emails.trim())) {
        setSnackbar({
          open: true,
          message: "Please enter valid email addresses",
          severity: 'error'
        });
        return false;
      }
    }
    
    // Check for duplicates
    const emails = filledEmails.map(item => item.emails.trim().toLowerCase());
    const uniqueEmails = [...new Set(emails)];
    
    if (emails.length !== uniqueEmails.length) {
      setSnackbar({
        open: true,
        message: "Email addresses must be unique",
        severity: 'error'
      });
      return false;
    }
    
    return true;
  };

  const handleSubmitinvite = async (e) => {
    e.preventDefault();
    
    if (!validateEmails()) return;
    
    setLoading(true);

    try {
      const response = await fetch(
        process.env.REACT_APP_CLIENT_EMAILINVITETALENTPARTNER,
        {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: token 
          },
          body: JSON.stringify({ 
            invite: invite.filter(item => item.emails.trim() !== ""), 
            id: user.data[0].id 
          })
        }
      );
      
      const json = await response.json();
      
      if (json.status == 200) {
        setSnackbar({
          open: true,
          message: "Invitation emails sent successfully!",
          severity: 'success'
        });
        
        // Reset form
        setinvite([{ emails: "" }]);
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else if (json.status == 400) {
        setSnackbar({
          open: true,
          message: json.data || "Failed to send invitations",
          severity: 'error'
        });
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: "Network error. Please try again.",
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue === 0) {
      history.push('/PartnerInvite');
    }
  };

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Fade in timeout={600}>
        <Box>
          {/* Navigation Tabs */}
          <Paper
            elevation={0}
            sx={{
              mb: 3,
              bgcolor: isDarkMode ? 'grey.900' : 'grey.50',
              borderRadius: 2,
              overflow: 'hidden'
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="fullWidth"
              sx={{
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 2
                },
                '& .Mui-selected': {
                  bgcolor: isDarkMode ? 'primary.dark' : 'primary.main',
                  color: '#fff !important'
                }
              }}
            >
              <Tab
                icon={<GroupIcon />}
                label={intl.formatMessage({ id: "managerInvite" })}
                iconPosition="start"
              />
              <Tab
                icon={<PersonIcon />}
                label={intl.formatMessage({ id: "TalentInvite" })}
                iconPosition="start"
              />
            </Tabs>
          </Paper>

          {/* Main Content */}
          <Paper
            elevation={3}
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              bgcolor: isDarkMode ? 'grey.900' : '#fff',
              boxShadow: isDarkMode 
                ? '0 8px 32px rgba(0,0,0,0.5)' 
                : '0 8px 32px rgba(0,0,0,0.1)'
            }}
          >
            {/* Header Section */}
            <Box
              sx={{
                background: isDarkMode
                  ? 'linear-gradient(135deg, #1e3c72, #2a5298)'
                  : 'linear-gradient(135deg, #00b4d8, #0077b6)',
                p: 3,
                color: '#fff'
              }}
            >
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                {intl.formatMessage({ id: "enterEmail" })} {intl.formatMessage({ id: "talentInvite" })}
              </Typography>
            </Box>

            {/* Form Section */}
            <Box sx={{ p: 3 }}>
              <form onSubmit={handleSubmitinvite}>
                <Box sx={{ mb: 3 }}>
                  {invite.map((x, i) => (
                    <Card
                      key={i}
                      elevation={0}
                      sx={{
                        mb: 2,
                        bgcolor: isDarkMode ? 'grey.800' : '#f8f9fa',
                        border: `1px solid ${isDarkMode ? 'grey.700' : '#e9ecef'}`,
                        '&:hover': {
                          boxShadow: isDarkMode 
                            ? '0 4px 12px rgba(255,255,255,0.1)' 
                            : '0 4px 12px rgba(0,0,0,0.1)'
                        },
                        transition: 'box-shadow 0.3s ease'
                      }}
                    >
                      <CardContent sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <EmailIcon 
                            sx={{ 
                              color: isDarkMode ? 'primary.light' : 'primary.main',
                              fontSize: '1.5rem'
                            }} 
                          />
                          <TextField
                            fullWidth
                            type="email"
                            placeholder={intl.formatMessage({ id: "enterEmail" })}
                            name='emails'
                            value={x.emails}
                            onChange={(e) => handleinputchange(e, i)}
                            required
                            variant="outlined"
                            size="medium"
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 2,
                                bgcolor: isDarkMode ? 'grey.700' : '#fff'
                              }
                            }}
                          />
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {invite.length - 1 === i && invite.length < 3 && (
                              <Tooltip title="Add another email">
                                <IconButton
                                  onClick={handleaddclick}
                                  sx={{
                                    bgcolor: isDarkMode ? 'success.dark' : 'success.main',
                                    color: '#fff',
                                    '&:hover': {
                                      bgcolor: isDarkMode ? 'success.darker' : 'success.dark'
                                    }
                                  }}
                                >
                                  <AddIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {invite.length !== 0 && (
                              <Tooltip title="Remove this email">
                                <IconButton
                                  onClick={() => handleremove(i)}
                                  sx={{
                                    bgcolor: isDarkMode ? 'error.dark' : 'error.main',
                                    color: '#fff',
                                    '&:hover': {
                                      bgcolor: isDarkMode ? 'error.darker' : 'error.dark'
                                    }
                                  }}
                                >
                                  <RemoveIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* Submit Button */}
                <Box sx={{ textAlign: 'center' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    size="large"
                    disabled={loading}
                    startIcon={loading ? null : <SendIcon />}
                    sx={{
                      minWidth: 200,
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      background: isDarkMode
                        ? 'linear-gradient(45deg, #1976d2, #1565c0)'
                        : 'linear-gradient(45deg, #1dbf73, #17a05d)',
                      boxShadow: '0 4px 15px rgba(29, 191, 115, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 20px rgba(29, 191, 115, 0.4)',
                        transform: 'translateY(-1px)'
                      },
                      '&:disabled': {
                        bgcolor: 'grey.400'
                      }
                    }}
                  >
                    {loading ? "Sending..." : intl.formatMessage({ id: "invite" })}
                  </Button>
                </Box>
              </form>
            </Box>
          </Paper>
        </Box>
      </Fade>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default PartnerINviteTalent;